import React from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import arowDownLottie from 'src/assets/arrow-down.json';
import bgLottie from 'src/assets/bg-lottie.json';
import logoLottie from 'src/assets/logo-lottie.json';
import LiquidGlassEffect from 'src/components/common/LiquidGlassEffect';
import { getTotalPrayerHubsLabel } from 'src/utils/get-total-prayer-hubs-label';

import css from './Hero.module.scss';

const BG_LOTTIE_COVER = 'xMidYMid slice';
const MESSAGE_ROTATE_MS = 5000;
const FADE_MS = 280;
const INITIAL_GIVING_TIME = { days: 22, hours: 5, minutes: 37 };

type GivingTime = typeof INITIAL_GIVING_TIME;

function addMinute({ days, hours, minutes }: GivingTime): GivingTime {
  let nextMinutes = minutes + 1;
  let nextHours = hours;
  let nextDays = days;

  if (nextMinutes >= 60) {
    nextMinutes = 0;
    nextHours += 1;
  }
  if (nextHours >= 24) {
    nextHours = 0;
    nextDays += 1;
  }

  return { days: nextDays, hours: nextHours, minutes: nextMinutes };
}

function formatGivingTime({ days, hours, minutes }: GivingTime) {
  return `צברנו יחד ${days} ימים, ${hours} שעות ו-${minutes} דקות של נתינה לאחר`;
}

type Props = {
  totalPrayDuration?: number;
  title?: string;
  quote?: string;
  connectedToday?: number;
  onConnectClick?: () => void;
  playKey?: number;
  isPlaying?: boolean;
  onAnimationComplete?: () => void;
  onScrollDownClick?: () => void;
};

export default function Hero({
  title = 'בליבי',
  quote = 'כל המתפלל על חברו נענה תחילה. הוא לא משנה רק גורל של אדם אחד, הוא מעורר רחמים על העולם כולו.',
  connectedToday = 0,
  onConnectClick,
  playKey = 0,
  totalPrayDuration = 0,
  isPlaying = false,
  onAnimationComplete,
  onScrollDownClick,
}: Props) {
  const lottieRef = React.useRef<LottieRefCurrentProps>(null);
  const lottieBgRef = React.useRef<LottieRefCurrentProps>(null);
  const [connectedCount, setConnectedCount] = React.useState(connectedToday);
  const [givingTime, setGivingTime] = React.useState(INITIAL_GIVING_TIME);
  const [messageIndex, setMessageIndex] = React.useState(0);
  const [isFading, setIsFading] = React.useState(false);

  React.useEffect(() => {
    setConnectedCount(connectedToday);
  }, [connectedToday]);

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleTick = () => {
      const delayMs = 2000 + Math.random() * 4000;
      timeoutId = setTimeout(() => {
        setConnectedCount(prev => prev + 1 + Math.floor(Math.random() * 3));
        scheduleTick();
      }, delayMs);
    };

    scheduleTick();
    return () => clearTimeout(timeoutId);
  }, []);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setGivingTime(prev => addMinute(prev));
    }, 60_000);

    return () => clearInterval(intervalId);
  }, []);

  React.useEffect(() => {
    let fadeTimeoutId: ReturnType<typeof setTimeout>;

    const rotateId = setInterval(() => {
      setIsFading(true);
      fadeTimeoutId = setTimeout(() => {
        setMessageIndex(prev => (prev + 1) % 2);
        setIsFading(false);
      }, FADE_MS);
    }, MESSAGE_ROTATE_MS);

    return () => {
      clearInterval(rotateId);
      clearTimeout(fadeTimeoutId);
    };
  }, []);

  const formattedCount = new Intl.NumberFormat('he-IL').format(connectedCount);
  const connectedMessage = `${formattedCount} אנשים התחברו לכאב של האחר`;
  const givingMessage = getTotalPrayerHubsLabel(totalPrayDuration);
  const displayMessage = messageIndex === 0 ? connectedMessage : givingMessage;

  const playBoth = React.useCallback(() => {
    lottieRef.current?.stop();
    lottieBgRef.current?.stop();
    lottieBgRef.current?.goToAndPlay(0, true);
    lottieRef.current?.goToAndPlay(0, true);
  }, []);

  React.useEffect(() => {
    if (!playKey) return;
    playBoth();
  }, [playKey, playBoth]);

  const handleLottieComplete = React.useCallback(() => {
    if (!isPlaying || !playKey) return;
    onAnimationComplete?.();
  }, [isPlaying, onAnimationComplete, playKey]);

  return (
    <section className={css.hero}>
      <div className={css.animatedBg} aria-hidden>
        <Lottie
          className={css.lottieBg}
          animationData={bgLottie}
          loop={false}
          autoplay={false}
          lottieRef={lottieBgRef}
          rendererSettings={{ preserveAspectRatio: BG_LOTTIE_COVER }}
          onComplete={() => lottieBgRef.current?.stop()}
        />
      </div>
      <div className={css.logo} aria-hidden>
        <Lottie
          animationData={logoLottie}
          loop={false}
          autoplay={false}
          lottieRef={lottieRef}
          onComplete={handleLottieComplete}
        />
      </div>
      <h1 className={css.title}>{title}</h1>
      <p className={css.quote}>{quote}</p>
      <LiquidGlassEffect className={css.amountGlass}>
        <div className={css.amountWrap}>
          <div className={`${css.amount} ${isFading ? css.amountFading : ''}`} aria-live="polite">
            {displayMessage}
          </div>
        </div>
      </LiquidGlassEffect>
      <div className={css.scrollDownBtn} onClick={onScrollDownClick}>
        <Lottie animationData={arowDownLottie} />
      </div>
    </section>
  );
}
